import { Form } from 'antd';
import { FormProviderProps } from 'antd/lib/form/context';
import dayjs from 'dayjs';
import { PropsWithChildren, createContext, useContext, useState } from 'react';

import 'technical/time-utils/dayjs.timezone';
import { DateSelectionTypeEnum } from 'ui/form/temporality-select-input';

import { ProdAndPerfRangeFilters } from './types';

const FILTERS_FORM_NAME = 'production-and-performance-filters-form';

const defaultFilters = {
  filterType: 'date',
  dateRange: [null, null],
  dayMode: 'shift',
  showTotalDurations: undefined,
  durationInterval: null,
  withProjection: false,
} satisfies ProdAndPerfRangeFilters;

const ProdAndPerfFiltersContext =
  createContext<ProdAndPerfRangeFilters>(defaultFilters);

const ProdAndPerfFiltersProvider = ({
  initialFilters,
  noDefaultFor,
  children,
}: PropsWithChildren<{
  initialFilters: ProdAndPerfRangeFilters;
  noDefaultFor?: DateSelectionTypeEnum[];
}>) => {
  const [filters, setFilters] =
    useState<ProdAndPerfRangeFilters>(initialFilters);

  const onFormChange: FormProviderProps['onFormChange'] = (
    formName,
    { forms },
  ) => {
    if (formName === FILTERS_FORM_NAME) {
      const form = forms[formName];

      const dateRange =
        form.getFieldValue('filterType') === 'date' &&
        // Check for `period` included in noDefaultFor to empty the input when
        // it is changed back to date filter type from ring filter type
        noDefaultFor?.includes(DateSelectionTypeEnum.Period)
          ? [null, null]
          : [dayjs().tz().subtract(1, 'week'), dayjs().tz()];

      const values = {
        dayMode: 'shift',
        dateRange,
        ringRange: [null, null],
        ...form.getFieldsValue(),
      };

      // Setting default range for both day and ring because Antd Form remove the fields value if the input is
      // not rendered. Since we conditionnaly render the dateRange or ringRange depending on the `filterType`
      // it is required to add them with defaut value to the FiltersProvider. Therefore, we're sure it is always
      // defined when the filterType is changed
      setFilters(values);
      form.setFieldsValue(values);
    }
  };

  return (
    <ProdAndPerfFiltersContext.Provider value={filters}>
      <Form.Provider onFormChange={onFormChange}>{children}</Form.Provider>
    </ProdAndPerfFiltersContext.Provider>
  );
};

const useProdAndPerfFiltersFormName = () => FILTERS_FORM_NAME;

const useProdAndPerfFilters = () => useContext(ProdAndPerfFiltersContext);

export {
  ProdAndPerfFiltersProvider,
  useProdAndPerfFiltersFormName,
  useProdAndPerfFilters,
};
